import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

/** Um `primary` por tela; o resto é `secondary`, `tinted` ou `ghost` (docs/DESIGN-SYSTEM.md › Botão). */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-semibold transition-[filter,transform,background-color] duration-150 ease-out hover:brightness-[1.04] active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green disabled:pointer-events-none disabled:opacity-45 motion-reduce:transition-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-green-gradient text-on-green shadow-green",
        secondary: "bg-surface text-label shadow-control",
        tinted: "bg-green-tint text-green",
        ghost: "text-label hover:bg-fill",
        destructive: "bg-crit text-on-green",
        link: "h-auto px-0 text-green underline-offset-4 hover:underline active:scale-100",
      },
      size: {
        md: "h-control-md rounded-md px-4 text-body",
        sm: "h-control-sm rounded-sm px-3 text-subhead",
        icon: "size-control-md rounded-md",
        "icon-sm": "size-control-sm rounded-sm",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Troca o primeiro ícone por um spinner e mantém o texto: o botão não muda de largura. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && !asChild ? (
          <>
            <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden />
            {withoutLeadingIcon(children)}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

function withoutLeadingIcon(children: React.ReactNode): React.ReactNode {
  const list = React.Children.toArray(children);
  const first = list[0];
  const isIcon = React.isValidElement(first) && typeof first.type !== "string";
  return isIcon && list.length > 1 ? list.slice(1) : children;
}

export { Button, buttonVariants };
